import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Baby, Brain, Flame, Move, Hand, Handshake, Footprints, Activity, Stethoscope, Shirt, CircleDot, Droplets, ScanLine, Bandage, Layers, Tag, Bug, Slice, Dna, SquareStack } from "lucide-react";
import SEO from "@/components/SEO";
import { WHATSAPP_URL as WHATSAPP_LINK, TEL_URL as PHONE_LINK } from "@/constants/contact";

const procedures = [
  { name: "Cleft Lip & Palate", description: "Surgical correction for children and adults", link: "/cleft-lip-palate-surgery-in-mumbai", icon: Baby },
  { name: "Craniosynostosis", description: "Skull reshaping surgery for abnormal head shape", link: "/craniosynostosis-surgery-in-mumbai", icon: Brain },
  { name: "Burns & Burn Scar Treatment", description: "Acute burn care, grafting, and advanced burn scar management", link: "/burns-burn-scar-treatment-mumbai", icon: Flame },
  { name: "Skin Grafting Surgery", description: "Autologous skin transfer for burns, wounds, and surgical defects", link: "/skin-grafting-surgery-mumbai", icon: SquareStack },
  { name: "Post-Burn Contracture", description: "Releasing tight scar tissue limiting movement", link: "/post-burn-contracture-surgery-in-mumbai", icon: Move },
  { name: "Limb Salvage", description: "Preserving limbs threatened by trauma or disease", link: "/limb-salvage-surgery-in-mumbai", icon: Hand },
  { name: "Replantation", description: "Reattachment of severed fingers, hands, or limbs", link: "/replantation-surgery-in-mumbai", icon: Handshake },
  { name: "Diabetic Foot", description: "Surgical management preventing amputation", link: "/diabetic-foot-surgery-in-mumbai", icon: Footprints },
  { name: "Migraine Surgery", description: "Surgical relief for chronic migraine sufferers", link: "/migraine-surgery-in-mumbai", icon: Activity },
  { name: "Fibroadenoma", description: "Diagnosis, observation, and removal of benign breast lumps", link: "/fibroadenoma-breast-lump-treatment-mumbai", icon: Stethoscope },
  { name: "Lipoma Removal", description: "Benign fatty lumps — observation or surgical excision with histology", link: "/lipoma-removal-surgery-mumbai", icon: CircleDot },
  { name: "Sebaceous Cyst Removal", description: "Complete epidermoid cyst excision — wall removal to prevent recurrence", link: "/sebaceous-cyst-removal-mumbai", icon: Droplets },
  { name: "Mole Removal", description: "Cosmetic and medical mole removal — dermoscopy, excision, RF, and laser", link: "/mole-removal-mumbai", icon: ScanLine },
  { name: "Corn Removal", description: "Foot corn treatment — clinical paring, surgical excision, and prevention", link: "/corn-removal-treatment-mumbai", icon: Bandage },
  { name: "Keloid Treatment", description: "Combination therapy for keloid scars — injections, surgery, and adjuvant care", link: "/keloid-treatment-mumbai", icon: Layers },
  { name: "Scar Management", description: "Acne, surgical, burn, and keloid scars — individualized combination therapy and honest outcome planning", link: "/scar-management-treatment-mumbai", icon: ScanLine },
  { name: "Skin Tag Removal", description: "RF, cautery, cryotherapy, or snip excision — quick clinic treatment", link: "/skin-tag-removal-mumbai", icon: Tag },
  { name: "Wart Removal", description: "HPV wart treatment — cryotherapy, RF, laser, and excision", link: "/wart-removal-mumbai", icon: Bug },
  { name: "Nail Removal Surgery", description: "Ingrown toenail — avulsion, matrixectomy, and nail bed surgery", link: "/nail-removal-surgery-mumbai", icon: Slice },
  { name: "Neurofibroma Removal", description: "Benign nerve sheath tumors — excision, histology, and NF1 assessment", link: "/neurofibroma-removal-mumbai", icon: Dna },
  { name: "Axillary Breast Removal", description: "Accessory breast tissue in the underarm — excision and liposuction", link: "/axillary-breast-removal-mumbai", icon: Shirt },
];

const reconstructiveStructuredData = {
  "@context": "https://schema.org",
  "@type": "MedicalWebPage",
  name: "Reconstructive Surgery — Dr. Vicky Jain",
  url: "https://drvickyjain.com/reconstructive-surgery-in-mumbai",
  description: "Expert reconstructive surgery by Dr. Vicky Jain in Mumbai covering burns, cleft lip and palate, craniosynostosis, limb salvage, diabetic foot, and complex trauma cases.",
  medicalAudience: { "@type": "MedicalAudience", audienceType: "Patient" },
  about: { "@type": "MedicalCondition", name: "Reconstructive Surgery" },
  author: { "@type": "Physician", name: "Dr. Vicky Jain", url: "https://drvickyjain.com/about" },
};

const ReconstructiveSurgeryPage = () => {
  return (
    <div className="bg-warm-white min-h-screen">
      <SEO title="Reconstructive Surgery in Mumbai | Dr. Vicky Jain" description="Expert reconstructive surgery by Dr. Vicky Jain. Treatment for burns, cleft lip, craniosynostosis, limb salvage, diabetic foot, and complex trauma cases." canonical="/reconstructive-surgery-in-mumbai" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reconstructiveStructuredData) }} />
      <section className="bg-warm-white pt-32 pb-20 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <p className="text-sm font-medium tracking-widest text-gold uppercase mb-4">Reconstructive Surgery</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-premium-purple mb-6 max-w-3xl">
            Reconstructive Surgery
          </h1>
          <p className="text-lg text-deep-navy/70 max-w-2xl leading-relaxed">
            Expert reconstruction for trauma, burns, congenital conditions, and complex cases. Dr. Vicky Jain&apos;s dual expertise in cosmetic and reconstructive surgery ensures both function and appearance are restored.
          </p>
        </div>
      </section>

      <section className="bg-warm-white py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {procedures.map((procedure, index) => (
              <div key={index} className="bg-white p-8 border border-deep-navy/10">
                <procedure.icon className="w-7 h-7 text-gold mb-5" strokeWidth={1.5} />
                <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
                <p className="text-deep-navy/70 leading-relaxed mb-6">{procedure.description}</p>
                <Link to={procedure.link} className="text-gold font-medium hover:text-deep-navy transition-colors">
                  Learn More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-premium-purple py-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-serif text-3xl text-warm-white mb-4">Book Your Consultation</h2>
              <p className="text-warm-white/70 mb-2">
                Reconstructive cases are evaluated individually. Contact us to discuss your specific situation.
              </p>
              <p className="text-warm-white/70">Every treatment starts with a detailed, one-on-one consultation.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild className="btn-gold border-none rounded-none px-8 py-6">
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">Book Consultation</a>
              </Button>
              <Button asChild variant="outline" className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6">
                <a href={PHONE_LINK}><Phone className="w-4 h-4 mr-2" />Call Now</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReconstructiveSurgeryPage;
